import { Interactable } from "@interactjs/types/index";
import { Field } from "./field.model";
import { Squad, SquadPlayer } from "./squad.model";
import { IStats, UbikoStats } from "./ubiko-stats.model";
import { ElementRef } from "@angular/core";
import { Kit } from "./kit.model";

export class Session {

    public static STARTED = 'started';
    public static COMPUTED = 'computed';
    public static FINISHED = 'finished';
    public static TYPE_MATCH = 'match';
    public static TYPE_MATCH_TRAINING = 'match_training';
    public static TYPE_TRAINING = 'training';

    id: string;
    name: string; 
    created: string;  
    finished: string;
    status: string;
    squad: Squad;
    score?: Score;
    params?: any;
    typeInfo: TypeInfo;
    training_type: string;
    field: Field;
    ubiko_stats: UbikoStats;
    parts?: Part[];
    rpe?: SessionRPE;
    kit: SessionKit;
    _devices: any;
    source: string;
    live_preview?: boolean;
    constructor() {}      
}

export interface Part {
    id: string;
    name: string; 
    start: string;
    end: string;
    status: string;
    ubiko_stats?: IStats;
    _players: PartPlayer[];
    players: any;
    //stats: IStats;   
    events: any[];
    group?: string;
}

export interface PartPlayer {
    id: string;
    ubiko_stats: IStats;    
    start: string;
    end: string;
    device?: string;
    data: {
        heatmap?: string;
    }
    clone?: string;
}

export interface SessionRPE {
    rpe?: number;
}

export interface TypeInfo {
    name: string;
    type: string;
}

export interface SessionKit {
    id: string;
    type: string;
}

export interface Score {
    goals: number;
    opponent_goals: number;
}

export function completeSession(session: Session, completeParts?: boolean) {
    if(session.ubiko_stats) {
        session.ubiko_stats._players = [];
        if(session.ubiko_stats.players) {
            for (let [key, value] of Object.entries(session.ubiko_stats.players)) {        
                let stats = value as IStats;
                stats.id_player = key;
                session.ubiko_stats._players.push(stats);
            }
        }
    }
    //session._parts = [];
    if(completeParts && session.parts) {        
        session.parts.forEach(part => {   
            part._players = [];
            for (let value of Object.values(part.players)) {  
                part._players.push(value as PartPlayer);
            }
        });
    }
}

export interface SessionPlayer {   
    player: SquadPlayer; 
    id_player: string;
    group: string;
    availability: string;
    previewSpeeds?: PreviewSpeed;
    parts?: PartBuilder[];
    class?: string;
    start?: Date;
    end?: Date;
    device?: DeviceInfo;
    chart?: DeviceChart, 
    sectionEl?: ElementRef;   
    inSession?: boolean;
}

export interface SessionGlobal {    
    name: string;    
    start: Date;
    end: Date;    
    parts: PartBuilder[];    
    chart?: DeviceChart,
    class?: string;  
    speeds?: SpeedInstant[];
}

export interface Speed {
    value: number;
    timestamp: number;
}

export interface PreviewSpeed {
    max: number;
    speeds: Speed[];
}

export interface SpeedPoint {
    value: number | string;
    position: number;
}

export interface SpeedInstant {
    winStart: number;
    winEnd: number;
    timestamp: number;
    speeds: number[];
    average: number;
}

interface DeviceChart {
    x_axis: SpeedPoint[];
    y_axis: SpeedPoint[];
    points: string[];
    initialized?: boolean;
}

export interface DeviceInfo {
    id: string;
    players?: SquadPlayer[];    
    name: string;
    completed: boolean;
    start?: Date;
    end?: Date; 
    preview?: Preview;
    disabled?: boolean;
}

export interface DeviceAssignment {    
    id_player: string;
    id_device?: string;   
    group: string;
    availability: string;
}

export interface SessionGroup {
    group: ConfigGroup;
    global?: SessionGlobal;
    selected?: boolean;
}

export interface ConfigGroup {
    id: string;
    name: string;    
    order: number;
    default?: boolean;    
}

export interface ConfigType {
    id: string;
    name: string; 
    default?: boolean;    
}

export interface ConfigGroupResponse {
    [id: string]: GroupEntry;
}

export interface GroupEntry {
    order: number;
    default?: boolean;
    translations: {
        [lang: string]: GroupTranslation;
    };
}

export interface GroupTranslation {
    name: string;
    description: string;
}

export interface ConfigAvailabilityResponse {
    [id: string]: AvailabilityEntry;
}

export interface AvailabilityEntry {
    default?: boolean;
    translations: {
        [lang: string]: AvailabilityTranslation;
    };
}

export interface AvailabilityTranslation {
    name: string;
    description: string;
}

export interface ConfigAvailability {
    id: string;
    name: string;
    description: string;
    default?: boolean;
}

export interface ConfigTrainingType {
    id: string;
    name: string;
    default?: boolean;
}

export interface TabState {
    active: SessionTab;
    previous?: SessionTab;
}

export interface SessionTab {
    name: string;
    template: string;
    icon: string;
    active?: boolean;
    display?: boolean;
    disabled?: boolean;
    completed?: boolean;
    warning?: string;
}

export enum TabNames {
    kit = 'kit',
    upload = 'upload',
    data = 'data',
    players = 'players',
    tasks = 'tasks'
}

export interface BuilderEvent {
    subscription?: {
        players?: boolean;
        tasks?: boolean;
    }    
    detectChanges?: boolean;
}

export interface BuilderState {  
    changes: {
        data: boolean;
        players: boolean;
        tasks: boolean;
    }
    reload: {
        players: string[];      
        preprocessed: boolean;
    }
    errors: {
        data: string[];
        players: string[];
        tasks: string[];
    }    
    warnings: {
        data: string[];
        players: string[];
        tasks: string[];
    }  
}

export interface SessionRequest {   
    created: Date;
    finished: Date;
    type?: string;
    field?: string;
    kitSelected?: Kit;
    kit: string;
    name?: string;
    squad: string;
    status?: string;
    params?: {
        rpe: number;
        opponent?: {
            name: string;
            shield: string;
        }
    };
    training_type?: string;
    score?: {
        goals: number;
        opponent_goals: number;
    }
    assignments?: DeviceAssignment[];
    autocompute?: boolean;
    events?: SessionEvent[];
    parts?: PartRequest[]; // TODO
}

export interface PartRequest {   
    _id_part: string; 
    start: string;
    end: string;
    name: string;
    group: string;
    players: PartPlayerRequest[];
    id?: string;    
}

export interface PartPlayerRequest {
    id: string;
    start: string;
    end: string;
    device: string;
}

export interface SessionEvent {
    id: string;
    player: string;
    player2: string;
    minute: number;
    instant: string;
    playerData: any;
    player2Data: any;
}

export interface PartBuilder {
    id_part: string;    
    group: string;
    startStep: number;
    endStep: number;
    start: string;
    end: string;
    duration: string;
    width: number;
    left: number;
    name: string;
    editMode?: boolean,    
    new?: boolean;
    interactable: Interactable | undefined;
}

export interface PartRestrinction {
    id_part: string;
    left: number;
    right: number;
}

export interface ResizableResponse {
    interactable: Interactable;
    /*results: {
        periodsChanged: boolean;
        currentlyDragged: boolean;
    }*/
}

export interface ResizableEvent {    
    id_part: string;
    //global: {
    //    start: Date;
    //    end: Date;        
    //}
    //players: ElementState[];
    applyRestrinctions?: boolean;
}

export interface ElementState {    
    id: string;
    start: Date;
    end: Date;    
}

export interface Preview {
    device: {
        id: string;
        name: string;
    }
    data: PreviewData[];
    player?: {
        id: string;
    }
}

export interface PreviewData {
    t: number;
    s: number;
}

export interface TimeWindow {
    start: Date;
    end: Date;
    duration: number;
    speedAvg?: number;    
}

export interface PreviewDeviceStatus {
    id: string;
    completed: boolean;
}

export interface InvalidPart {
    name: string; 
    type: string;
}

export interface WeekDay {
    day: number;
    name: string;
    date: {
        d: number;
        m: number; 
        y: number;
    },
    active: boolean;
}
